import { readCacheOwner, resolveOwnershipAction, writeCacheOwner } from './cache-ownership';

/** Builds the `prepare` hook passed to a freshly built synced store:
 *  reconciles the local caches' owner stamp against `userId` before any
 *  read/write is allowed through (SyncedDocStore awaits this at the top of
 *  every method — see its `ready` gating). Unowned (pre-ownership) data is
 *  claimed by the first signed-in user; a cache already owned by someone
 *  else means a different account signed in on this browser — `wipeCaches`
 *  runs first so the incoming user's documents re-sync clean instead of
 *  inheriting (and re-uploading) the previous user's data. An unreadable
 *  owner stamp is treated as 'keep' (no-wipe-on-doubt — see
 *  readCacheOwner's contract). `wipeCaches` must clear EVERY owned local
 *  cache (all synced IndexedDB databases), not just the calling store's. */
export function makeOwnershipPrepare(
	userId: string,
	wipeCaches: () => Promise<void>,
	deps: { readOwner?: () => string | null | 'unreadable' } = {},
): () => Promise<void> {
	const readOwner = deps.readOwner ?? readCacheOwner;
	return async () => {
		const storedOwner = readOwner();
		if (storedOwner === 'unreadable') {
			console.debug('ownership-prepare: cache owner stamp unreadable, keeping local cache as-is');
			return;
		}
		const action = resolveOwnershipAction(storedOwner, userId);
		if (action === 'wipe-and-claim') {
			await wipeCaches();
			writeCacheOwner(userId);
		} else if (action === 'claim') {
			writeCacheOwner(userId);
		}
		// 'keep': the cache already belongs to this user — nothing to do.
	};
}
